import { Link } from 'react-router-dom';
import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { FadeInSection } from '@/components/animations/FadeInSection';
import { ShimmerOnScroll } from '@/components/animations/ShimmerOnScroll';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  BookOpen, 
  Rocket, 
  RefreshCw, 
  Droplet,
  Droplets,
  Flame, 
  Shield, 
  Wallet,
  DollarSign,
  Settings,
  Search,
  Coins, 
  LockIcon, 
  BarChart3Icon, 
  CheckCircle,
  Percent,
  ArrowLeftRight,
  BadgeCheck,
  AlertTriangle,
  LineChart,
  Plus,
  Minus,
  Lock,
  FileCode2,
  Users,
  TrendingUp,
  Gift
} from 'lucide-react';
import { FaHandshake, FaMoneyBillWave } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '@/providers/ThemeProvider';
import { MetaMaskIcon } from '@/components/icons/MetaMask';
import { WalletConnectIcon } from '@/components/icons/WalletConnect';
import { RocketCoin } from '@/components/icons/RocketCoin';
import { useState } from 'react';
import AppLoadingScreen from '@/components/AppLoadingScreen';
import { NetworkCarousel } from '@/components/home/NetworkCarousel';


const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hoverBgColor = 'hover:bg-accent/10';
  const textColor = 'text-foreground';
  const textColorSecondary = 'text-muted-foreground';

  return (
    <div
      className="border-b last:border-b-0"
      style={{ borderColor: 'color-mix(in srgb, var(--color-header-accent, #0A74FF) 30%, transparent)' }}
    >
      <button
        className={`w-full py-5 px-4 flex justify-between items-center ${hoverBgColor} transition-colors duration-200`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`text-lg font-medium ${textColor}`}>{question}</span>
        <span 
          className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-header-accent, #0A74FF)' }}
        >
          ▼
        </span>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 py-4 px-4' : 'max-h-0'
        }`}
      >
        <p className={textColorSecondary}>{answer}</p>
      </div>
    </div>
  );
};

const faqData = [
  {
    question: "What is MdbApp Token Creator?",
    answer: "MdbApp Token Creator is a professional platform for creating and deploying ERC20 and BEP20 tokens across multiple blockchains including Ethereum and BNB Smart Chain. Our platform simplifies the token creation process with a user-friendly interface."
  },
  {
    question: "What are ERC-20 and BEP-20 Tokens?",
    answer: "ERC-20 and BEP-20 are token standards for the Ethereum and BNB Smart Chain networks respectively. These tokens are blockchain-based assets that can be sent, received, and stored like any cryptocurrency, following specific protocol standards for compatibility and security."
  },
  {
    question: "What types of tokens can I create?",
    answer: "You can create several types of tokens: Taxable tokens with configurable tax rates, Dividend tokens that reward holders, Deflationary tokens with burn mechanisms, Fixed Supply tokens, Capped Supply tokens, and Unlimited Supply tokens. Each type serves different project needs and use cases."
  },
  {
    question: "What are my total supply options?",
    answer: "We offer Fixed Supply (unchangeable total supply) The initial supply is sent to the owner's wallet upon creation."
  },
  {
    question: "Can I modify token settings after deployment?",
    answer: "Yes, you can use token tools to adjust/modify your token settings such as Tax Rates, Transfering ownership ETC, And you can use the Smart Contract locker to Lock specific features, Master Lock the contract, Or even renounce ownership."
  },
  {
    question: "Who can Burn Tokens?",
    answer: "Anyone can burn tokens if they have the token in thier wallet. This is not restricted to a contract owner. You can use token tools to burn tokens. Simply select Token tools, enter the tokens address, and select ammount to burn. This is a contract level burn and will reflect immediately across the blockchain."
  }
];

export function HomePage() {
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const title = "Create Crypto Tokens | MdbApp - Multi-Chain Token Generator";
  const description = "Create, launch, and manage verified tokens on Ethereum, BSC, Avalanche, Solana, and Sui networks with MdbApp. No coding required! Get started today!";
  const keywords = "token creator, crypto token generator, ERC20 token, BEP20 token, SPL token, Solana token, Sui token, BSC token creator, ETH token creator, AVAX token creator, Solana token creator, Sui token creator, cryptocurrency, smart contract generator, token launch platform, token management, DeFi token creator, custom token generator, token development, blockchain tokens, token deployment, token minting, token burning, liquidity management, anti-bot protection, token security, contract locking, contract locker, ownership renouncing, contract renouncement, lock liquidity, renounce ownership, multi-chain token";

  // Get current theme name from localStorage
  const currentThemeName = localStorage.getItem('currentThemeName') || '';
  
  // Use default blue-gold gradient for default theme, otherwise use theme colors
  const isDefaultTheme = currentThemeName === '' || currentThemeName === 'mdbapp-default';

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  return (
    <>
      {isLoading && <AppLoadingScreen onComplete={handleLoadingComplete} />}
      <SimpleSEO
        title={title}
        description={description}
        keywords={keywords}
      />

      {/* Hero Section */}
      <FadeInSection direction="up">
        <section className="pt-6 sm:pt-8 pb-2">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 
                className="mb-4 pb-2 text-4xl font-bold tracking-tight sm:text-6xl [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] [background-size:200%_auto] animate-gradient"
                style={{
                  backgroundImage: isDefaultTheme 
                    ? 'linear-gradient(to right, #0a74ff, #FFD700, #0a74ff)'
                    : `linear-gradient(to right, var(--color-header-accent, #0a74ff), var(--color-text, #e5e5e5), var(--color-header-accent, #0a74ff))`
                }}
              >
                Create & Manage Your Own Crypto Tokens
              </h1>
              <div className="space-y-2 mb-6">
                <div className="max-w-3xl mx-auto">
                  <p className="text-xl md:text-2xl text-muted-foreground flex items-center justify-center gap-2 flex-wrap">
                    Create, Launch, and Manage 
                    <span className="inline-flex items-center gap-1">
                      <span className="!text-green-500 group-hover:!text-green-400 transition-colors">Verified</span>
                      <BadgeCheck className="w-6 h-6 !text-green-500 group-hover:!text-green-400 transition-colors animate-pulse" />
                    </span>
                    Tokens on ETH/BSC/AVAX/Solana/Sui with Confidence
                  </p>
                  <p className="text-lg md:text-xl flex items-center justify-center gap-2 mt-4">
                    <span 
                      className="flex items-center gap-3 [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] [background-size:200%_auto] animate-gradient font-semibold"
                      style={{
                        backgroundImage: isDefaultTheme 
                          ? 'linear-gradient(to right, #0a74ff, #FFD700, #0a74ff)'
                          : `linear-gradient(to right, var(--color-header-accent, #0a74ff), var(--color-text, #e5e5e5), var(--color-header-accent, #0a74ff))`
                      }}
                    >
                      <FileCode2 
                        className="w-5 h-5" 
                        style={{
                          stroke: isDefaultTheme ? '#0a74ff' : 'var(--color-header-accent, #0a74ff)'
                        }}
                      /> 
                      No Coding Required 
                      <FileCode2 
                        className="w-5 h-5" 
                        style={{
                          stroke: isDefaultTheme ? '#FFD700' : 'var(--color-text, #e5e5e5)'
                        }}
                      />
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex justify-center gap-4 flex-wrap mb-6 mt-4">
                <ShimmerOnScroll>
                  <Button asChild size="lg" className="bg-transparent hover:bg-transparent border-[var(--color-header-accent,#0A74FF)] border text-foreground group">
                    <Link to="/create" className="flex items-center">
                      Create Your Token <RocketCoin className="ml-2 w-5 h-5 [color:#EAB308] group-hover:[color:#F59E0B]" />
                    </Link>
                  </Button>
                </ShimmerOnScroll>
                <ShimmerOnScroll>
                  <Button asChild size="lg" variant="outline">
                    <Link to="/blog" className="flex items-center">
                      Read Guides <BookOpen className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </ShimmerOnScroll>
              </div>
              <div className="mt-4 mb-0 flex justify-center items-center gap-8">
                <div className="flex items-center gap-2">
                  <MetaMaskIcon className="w-8 h-8" />
                  <span className="text-sm font-medium">MetaMask Verified</span>
                </div>
                <div className="flex items-center gap-2">
                  <WalletConnectIcon className="w-8 h-8" />
                  <span className="text-sm font-medium">WalletConnect Verified</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </FadeInSection>

      {/* Network Carousel Section */}
      <FadeInSection direction="up">
        <NetworkCarousel />
      </FadeInSection>

      {/* Get the App Section */}
      <FadeInSection direction="up">
        <section className="py-2 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img src="/FireGif.gif" alt="Fire" className="w-8 h-8" />
                <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 animate-gradient">Get the App</h2>
                <img src="/FireGif.gif" alt="Fire" className="w-8 h-8" />
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300">Access MDBApp on all your devices</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {/* Web App */}
                <div className="relative rounded-xl p-6 bg-transparent border-2 border-[var(--color-header-accent,#0A74FF)] hover:bg-white/5 transition-colors" style={{ boxShadow: '0 0 20px color-mix(in srgb, var(--color-header-accent, #0A74FF) 40%, transparent), 0 0 40px color-mix(in srgb, #ef4444 20%, transparent)' }}>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-transparent border border-[var(--color-header-accent,#0A74FF)] text-[var(--color-header-accent,#0A74FF)]">LIVE</span>
                  </div>
                  <div className="flex flex-col items-center text-center space-y-4">
                    <img 
                      src="/Dynamic Header Logos/MdbApp.io Default Logo.png" 
                      alt="MDBApp Logo" 
                      className="w-16 h-16"
                    />
                    <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0A74FF] to-red-500">Web App</h3>
                    <p className="text-sm text-muted-foreground">Use in any browser</p>
                    <ShimmerOnScroll className="w-full">
                      <Link to="/create" className="w-full">
                        <Button className="w-full bg-transparent border-2 border-[var(--color-header-accent,#0A74FF)] hover:bg-accent text-white rounded-lg">
                          <Rocket className="mr-2 w-4 h-4" /> Launch Now
                        </Button>
                      </Link>
                    </ShimmerOnScroll>
                  </div>
                </div>

                {/* Android App */}
                <div className="relative rounded-xl p-6 bg-transparent border-2 border-[var(--color-header-accent,#0A74FF)] hover:bg-white/5 transition-colors" style={{ boxShadow: '0 0 20px color-mix(in srgb, var(--color-header-accent, #0A74FF) 40%, transparent), 0 0 40px color-mix(in srgb, #ef4444 20%, transparent)' }}>
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-transparent border border-[var(--color-header-accent,#0A74FF)] text-[var(--color-header-accent,#0A74FF)]">NEW</span>
                  </div>
                  <div className="flex flex-col items-center text-center space-y-4">
                    <img 
                      src="/Google play store button icon.png" 
                      alt="Android App" 
                      className="w-[57.6px] h-[57.6px]"
                    />
                    <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0A74FF] to-red-500">Android App</h3>
                    <p className="text-sm text-muted-foreground">Download from Google Play</p>
                    <ShimmerOnScroll className="w-full">
                      <a 
                        href="https://play.google.com/store/apps/details?id=io.mdbtdj.mdbapp" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-full"
                      >
                        <Button className="w-full bg-transparent border-2 border-[var(--color-header-accent,#0A74FF)] hover:bg-accent text-white rounded-lg">
                          <ArrowRight className="mr-2 w-4 h-4" /> Download Now
                        </Button>
                      </a>
                    </ShimmerOnScroll>
                  </div>
                </div>

                {/* iOS App */}
                <div className="relative rounded-xl p-6 bg-transparent border-2 border-[var(--color-header-accent,#0A74FF)] hover:bg-white/5 transition-colors" style={{ boxShadow: '0 0 20px color-mix(in srgb, var(--color-header-accent, #0A74FF) 40%, transparent), 0 0 40px color-mix(in srgb, #ef4444 20%, transparent)' }}>
                  <div className="flex flex-col items-center text-center space-y-4">
                    <img 
                      src="/apple (2).svg" 
                      alt="iOS App" 
                      className="w-16 h-16 dark:brightness-0 dark:invert"
                    />
                    <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0A74FF] to-red-500">iOS App</h3>
                    <p className="text-sm text-muted-foreground">Download from App Store</p>
                    <Button 
                      disabled 
                      className="w-full bg-transparent border-2 border-gray-500 text-gray-400 cursor-not-allowed rounded-lg"
                    >
                      <svg className="mr-2 w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      Coming Soon
                    </Button>
                  </div>
                </div>
              </div>
          </div>
        </section>
      </FadeInSection>

      {/* Referral Program Section */}
      <FadeInSection direction="down">
        <section className="py-12 px-4">
          <style>{`
            @keyframes flipIcon {
              0% { transform: perspective(400px) rotateY(0); }
              100% { transform: perspective(400px) rotateY(360deg); }
            }
            .flip-animation {
              animation: flipIcon 3s infinite linear;
            }
          `}</style>
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">
              <span className="inline-block flip-animation">
                <FaHandshake className="inline text-[#0A74FF]" />
              </span>
              {" "}
              <span className="text-[#0A74FF]">Refer</span> <span className="text-white">&</span> <span className="text-green-500">Earn</span>
              {" "}
              <span className="inline-block flip-animation">
                <FaMoneyBillWave className="inline text-green-500" />
              </span>
            </h2>
            <div className="flex items-center justify-center gap-3 mb-12">
              <img src="/FireGif.gif" alt="Fire" className="w-6 h-6" />
              <p className="text-lg text-muted-foreground text-center">
                Earn 50% with Our Referral Program
              </p>
              <img src="/FireGif.gif" alt="Fire" className="w-6 h-6" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)', borderColor: 'rgba(34, 197, 94, 0.5)' }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FadeInSection direction="up" delay={0}>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-3 rounded-full bg-green-500/10">
                      <Users className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h2 className="font-semibold mb-2">Share Your Link</h2>
                      <p className="text-sm text-muted-foreground">Get your unique referral link and share it with friends</p>
                    </div>
                  </div>
                </FadeInSection>
              </motion.div>

              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px color-mix(in srgb, var(--color-header-accent, #0A74FF) 30%, transparent)', borderColor: 'var(--color-header-accent,#0A74FF)' }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FadeInSection direction="right" delay={120}>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-3 rounded-full bg-[#0A74FF]/10">
                      <Coins className="w-6 h-6 text-[#0A74FF]" />
                    </div>
                    <div>
                      <h2 className="font-semibold mb-2">They Create Tokens</h2>
                      <p className="text-sm text-muted-foreground">Your referrals create tokens using your link</p>
                    </div>
                  </div>
                </FadeInSection>
              </motion.div>

              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(234, 179, 8, 0.3)', borderColor: 'rgba(234, 179, 8, 0.5)' }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FadeInSection direction="down" delay={240}>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-3 rounded-full bg-yellow-500/10">
                      <TrendingUp className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <h2 className="font-semibold mb-2">Earn 50% Commission</h2>
                      <p className="text-sm text-muted-foreground">Get 50% of every token creation fee they pay</p>
                    </div>
                  </div>
                </FadeInSection>
              </motion.div>

              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(168, 85, 247, 0.3)', borderColor: 'rgba(168, 85, 247, 0.5)' }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FadeInSection direction="left" delay={360}>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-3 rounded-full bg-purple-500/10">
                      <Gift className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <h2 className="font-semibold mb-2">Instant Payouts</h2>
                      <p className="text-sm text-muted-foreground">Withdraw your earnings anytime with no minimum</p>
                    </div>
                  </div>
                </FadeInSection>
              </motion.div>
            </div>
            <div className="text-center mt-8">
              <ShimmerOnScroll>
                <Button asChild size="lg" className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white">
                  <Link to="/account" className="flex items-center">
                    Start Earning Now <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </ShimmerOnScroll>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Main Features Section */}
      <FadeInSection direction="down">
        <section className="py-12">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold mb-8 tracking-tight text-center">
              All-in-One Token Creation & Management Platform
            </h2>
          </div>
        </section>
      </FadeInSection>

      {/* Comprehensive Token Management */}
      <FadeInSection direction="left">
        <section className="pt-0">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-6">
              <h2 className="text-3xl font-bold tracking-tighter text-center mb-3">
                Comprehensive Token Management UI
              </h2>
              <p className="text-muted-foreground">
                 MdbApp is Everything you need to create, launch, and manage your crypto tokens with ease
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Create Token */}
              <Link to="/create" className="group p-6 rounded-xl border hover:bg-accent/50 transition-colors" style={{ boxShadow: '0 0 15px color-mix(in srgb, var(--color-header-accent, #0A74FF) 30%, transparent)', borderColor: 'var(--color-header-accent,#0A74FF)' }}>
                <FadeInSection direction="up" delay={0}>
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-2 rounded-lg bg-[#0A74FF]/10">
                    <Coins className="w-6 h-6 text-[#0A74FF]" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2 group-hover:text-[#0A74FF] transition-colors">Create Token</h2>
                    <p className="text-sm text-muted-foreground">Launch your own token with customizable features, tax settings, and anti-bot measures. <Link to="/create" className="text-[var(--color-header-accent,#0A74FF)]">Create your token now!</Link></p>
                  </div>
                </div>
                </FadeInSection>
              </Link>

              {/* Smart Contract Locker */}
              <Link to="/mdbmatrixcontractscan" className="group p-6 rounded-xl border hover:bg-accent/50 transition-colors" style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)', borderColor: 'rgba(34, 197, 94, 0.5)' }}>
                <FadeInSection direction="right" delay={100}>
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <LockIcon className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2 group-hover:text-green-500 transition-colors">Matrix Contract Scanner</h2>
                    <p className="text-sm text-muted-foreground">Advanced contract analysis tool that verifies ownership, functions, and security features. <Link to="/mdbmatrixcontractscan" className="text-[var(--color-header-accent,#0A74FF)]">Scan your contract now!</Link></p>
                  </div>
                </div>
                </FadeInSection>
              </Link>

              {/* Burn Tokens */}
              <Link to="/tokentools" className="group p-6 rounded-xl border hover:bg-accent/50 transition-colors" style={{ boxShadow: '0 0 15px rgba(249, 115, 22, 0.3)', borderColor: 'rgba(249, 115, 22, 0.5)' }}>
                <FadeInSection direction="down" delay={200}>
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-2 rounded-lg bg-orange-500/10">
                    <Flame className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2 group-hover:text-orange-500 transition-colors">Token Burn Portal</h2>
                    <p className="text-sm text-muted-foreground">Permanently reduce token supply with our secure burning mechanism. <Link to="/tokentools" className="text-[var(--color-header-accent,#0A74FF)]">Burn tokens now!</Link></p>
                  </div>
                </div>
                </FadeInSection>
              </Link>

              {/* Token Tools */}
              <Link to="/tokentools" className="group p-6 rounded-xl border hover:bg-accent/50 transition-colors" style={{ boxShadow: '0 0 15px rgba(168, 85, 247, 0.3)', borderColor: 'rgba(168, 85, 247, 0.5)' }}>
                <FadeInSection direction="left" delay={300}>
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <BarChart3Icon className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2 group-hover:text-purple-500 transition-colors">Advanced Token Tools</h2>
                    <p className="text-sm text-muted-foreground">Manage tax rates, transaction limits, and wallet restrictions with ease. <Link to="/tokentools" className="text-[var(--color-header-accent,#0A74FF)]">Explore token tools!</Link></p>
                  </div>
                </div>
                </FadeInSection>
              </Link>

              {/* Liquidity Management */}
              <Link to="/create" className="group p-6 rounded-xl border hover:bg-accent/50 transition-colors" style={{ boxShadow: '0 0 15px rgba(31, 199, 212, 0.3)', borderColor: 'rgba(31, 199, 212, 0.5)' }}>
                <FadeInSection direction="up" delay={400}>
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-2 rounded-lg bg-[#1FC7D4]/10">
                    <DollarSign className="w-6 h-6 text-[#1FC7D4]" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2 group-hover:text-[#1FC7D4] transition-colors">Liquidity Management</h2>
                    <p className="text-sm text-muted-foreground">Add liquidity on Uniswap/PancakeSwap with confidence. <Link to="/create" className="text-[var(--color-header-accent,#0A74FF)]">Add liquidity now!</Link></p>
                  </div>
                </div>
                </FadeInSection>
              </Link>

              {/* Security Features */}
              <div className="p-6 rounded-xl border" style={{ boxShadow: '0 0 15px color-mix(in srgb, var(--color-header-accent, #0A74FF) 30%, transparent)', borderColor: 'var(--color-header-accent,#0A74FF)' }}>
                <FadeInSection direction="right" delay={500}>
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Shield className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2">Enhanced Security</h2>
                    <p className="text-sm text-muted-foreground">Verified contracts with built-in protections against common vulnerabilities</p>
                  </div>
                </div>
                </FadeInSection>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Burn Token Section */}
      <FadeInSection direction="right">
        <section className="py-8 px-4">
          <div className="container mx-auto">
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold tracking-tighter text-center">
                  Token Burning Made Simple
                </h2>
                <p className="text-muted-foreground">
                  Permanently remove tokens from circulation with our secure burning mechanism. 
                  Reduce total supply, increase scarcity, and build trust with your community.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium">Permanent token removal</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium">Secure burning process</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <LineChart className="w-5 h-5 text-red-500" />
                    <span className="text-sm font-medium">Real-time supply updates</span>
                  </li>
                </ul>
                <div className="flex justify-center mt-6">
                  <Button variant="outline" className="gap-2" asChild>
                    <Link to="/tokentools">
                      <Flame className="w-4 h-4 text-yellow-500" /> Burn Tokens
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-64 rounded-lg animate-gradient bg-gradient-to-r from-red-500 via-yellow-500 to-red-500 border-2 border-yellow-500/20 flex items-center justify-center">
                  <Flame className="w-20 h-20 text-white" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Liquidity Management Section */}
      <FadeInSection direction="left">
        <section className="py-8 px-4">
          <div className="container mx-auto">
            <div className="grid gap-8 md:grid-cols-2 items-center">
              <div className="relative order-2 md:order-1">
                <div className="w-full h-64 rounded-lg animate-gradient bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 border-2 border-blue-500/20 flex items-center justify-center">
                  <Droplet className="w-20 h-20 text-white" />
                </div>
              </div>
              <div className="space-y-4 order-1 md:order-2">
                <h2 className="text-3xl font-bold tracking-tighter text-center">
                  Advanced Liquidity Initialization
                </h2>
                <p className="text-muted-foreground">
                  Launch your custom token with initial liquidity using comprehensive deploy tools. 
                  Add liquidity on Uniswap/PancakeSwap with confidence.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-blue-500" />
                    <span>Adds liquidity pairs</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-blue-500" />
                    <span>Initial liquidity</span>
                  </li>
                </ul>
                <div className="flex justify-center mt-6">
                  <Button variant="outline" className="gap-2" asChild>
                    <Link to="/create">
                      <Droplet className="w-4 h-4 text-blue-500" /> Launch with Liquidity
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Matrix Scanner Features */}
      <FadeInSection direction="up">
        <section className="py-12 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              MdbApp Matrix Contract Scanner
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <FadeInSection direction="left" delay={0}>
              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)', borderColor: 'rgba(34, 197, 94, 0.5)' }}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-3 rounded-full bg-green-500/10">
                    <Search className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2">Deep Contract Analysis</h2>
                    <p className="text-sm text-muted-foreground">Scans contract functions, ownership details, and security features</p>
                  </div>
                </div>
              </motion.div>
              </FadeInSection>

              <FadeInSection direction="up" delay={120}>
              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(234, 179, 8, 0.3)', borderColor: 'rgba(234, 179, 8, 0.5)' }}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-3 rounded-full bg-yellow-500/10">
                    <AlertTriangle className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2">Risk Assessment</h2>
                    <p className="text-sm text-muted-foreground">Identifies potential vulnerabilities and security risks</p>
                  </div>
                </div>
              </motion.div>
              </FadeInSection>

              <FadeInSection direction="right" delay={240}>
              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(59, 130, 246, 0.3)', borderColor: 'rgba(59, 130, 246, 0.5)' }}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="flex flex-col items-center text-center gap-4">
                  <div className="p-3 rounded-full bg-blue-500/10">
                    <CheckCircle className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h2 className="font-semibold mb-2">Ownership Verification</h2>
                    <p className="text-sm text-muted-foreground">Validates contract ownership and permission settings</p>
                  </div>
                </div>
              </motion.div>
              </FadeInSection>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Matrix Scanner Demo */}
      <style>{`
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .flash-animation {
          animation: flash 2s infinite;
        }
        .typing-animation {
          overflow: hidden;
          border-right: 2px solid transparent;
          white-space: pre-wrap;
          margin: 0 auto;
          letter-spacing: 0.1em;
        }
      `}</style>
      <FadeInSection direction="right">
        <section className="py-8 px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-8">
              <p className="text-muted-foreground">
                Deep contract analysis and security verification
              </p>
            </div>
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <div className="relative rounded-lg border bg-card p-6 font-mono text-sm overflow-hidden [&_*]:text-green-400" style={{ height: "400px", borderColor: 'var(--color-header-accent, #0A74FF)' }}>
                <div className="absolute top-4 left-6">
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="flex items-center gap-2 text-green-400 font-semibold mb-4"
                  >
                    <span className="text-green-400">• Matrix Owner</span> <CheckCircle className="w-4 h-4 text-green-400 flash-animation" />
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ 
                    opacity: 1,
                    transition: {
                      duration: 2,
                      ease: "linear"
                    }
                  }}
                  viewport={{ once: true }}
                  className="mt-8 text-green-400 typing-animation overflow-hidden"
                  style={{ height: "300px", color: "#4ade80" }}
                >
                  <motion.pre
                    initial={{ width: "0%" }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 2, ease: "linear" }}
                    style={{ display: "block", color: "#4ade80" }}
                    className="text-green-400"
                  >
                    {`{
  "isConnected": true,
  "isOwner": true,
  "userAddress": "0x1234...abcd",
  "network": "ETH/BSC",
  "contractType": {
    "isOwnable": true,
    "hasRBAC": false,
    "hasCustomLocks": false
  },
  "featureStatus": {
    "ownership": {
      "exists": true,
      "lockable": true,
      "isLocked": false,
      "value": "0x1234...abcd"
    }
  }
}`}
                  </motion.pre>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2, duration: 0.5 }}
                  className="absolute bottom-4 right-6 bg-green-500/10 text-green-400 px-4 py-2 rounded-md flash-animation"
                >
                  Success: Contract validated successfully
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      </FadeInSection>

      {/* Token Management Features */}
      <FadeInSection direction="down">
        <section className="py-12 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Advanced Token Management UI
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(10, 116, 255, 0.3)', borderColor: 'rgba(10, 116, 255, 0.5)' }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FadeInSection direction="up" delay={0}>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-3 rounded-full bg-[#0A74FF]/10">
                      <Percent className="w-6 h-6 text-[#0A74FF]" />
                    </div>
                    <div>
                      <h2 className="font-semibold mb-2">Tax Management</h2>
                      <p className="text-sm text-muted-foreground">Configure buy/sell taxes and fee distributions</p>
                    </div>
                  </div>
                </FadeInSection>
              </motion.div>

              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(168, 85, 247, 0.3)', borderColor: 'rgba(168, 85, 247, 0.5)' }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FadeInSection direction="right" delay={120}>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-3 rounded-full bg-purple-500/10">
                      <ArrowLeftRight className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <h2 className="font-semibold mb-2">Transaction Limits</h2>
                      <p className="text-sm text-muted-foreground">Set maximum transaction amounts and trading restrictions</p>
                    </div>
                  </div>
                </FadeInSection>
              </motion.div>

              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)', borderColor: 'rgba(34, 197, 94, 0.5)' }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FadeInSection direction="down" delay={240}>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-3 rounded-full bg-green-500/10">
                      <Wallet className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h2 className="font-semibold mb-2">Wallet Controls</h2>
                      <p className="text-sm text-muted-foreground">Modify Max Wallet holding, Max Buys, & Max Sells limits</p>
                    </div>
                  </div>
                </FadeInSection>
              </motion.div>

              <motion.div 
                className="p-6 rounded-xl border"
                style={{ boxShadow: '0 0 15px rgba(249, 115, 22, 0.3)', borderColor: 'rgba(249, 115, 22, 0.5)' }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <FadeInSection direction="left" delay={360}>
                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="p-3 rounded-full bg-orange-500/10">
                      <RefreshCw className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <h2 className="font-semibold mb-2">Trading Controls</h2>
                      <p className="text-sm text-muted-foreground">Enable/disable trading and configure anti-bot measures</p>
                    </div>
                  </div>
                </FadeInSection>
              </motion.div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Token Management Screenshots */}
      <FadeInSection direction="left">
        <section className="py-8 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">Token Management Tools</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">Powerful tools to manage, secure, and analyze your MdbApp custom tokens across multiple blockchains. Tools are designed for Contracts deployed by the MdbApp.io factory.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Token Tools Screenshot */}
              <div className="relative">
                <div className="mb-4 text-center">
                  <h3 className="text-xl font-semibold mb-2">
                    <span className="text-[#0A74FF]">Token</span>{" "}
                    <span className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] bg-clip-text text-transparent">Tools</span>
                  </h3>
                  <p className="text-gray-600 text-sm">Manage and configure your token's features. Scan/Analyze/Modify your contracts features.</p>
                </div>
                <Button asChild variant="ghost" className="w-full p-0 h-auto hover:bg-transparent">
                  <Link to="/tokentools" className="block">
                    <img 
                      src={theme === 'light'
                        ? "/MdbApp Screenshots Full/2_MdbApp.io_TokenTools_light.jpeg"
                        : "/MdbApp Screenshots Full/2_MdbApp.io_TokenTools.jpeg"
                      }
                      alt="MdbApp Token Tools - Manage and monitor your tokens with our comprehensive token management dashboard at mdbapp.io/tokentools" 
                      className="w-full h-[300px] object-cover object-top rounded-lg border-2 border-[#0A74FF]/20 hover:border-[#0A74FF]/40 transition-colors"
                    />
                  </Link>
                </Button>
              </div>
              {/* Smart Contract Locker Screenshot */}
              <div className="relative">
                <div className="mb-4 text-center">
                  <h3 className="text-xl font-semibold mb-2">
                    <span className="text-[#FF0000]">Smart</span>{" "}
                    <span className="text-white">Contract</span>{" "}
                    <span className="text-[#00FF00]">Locker</span>
                  </h3>
                  <p className="text-gray-600 text-sm">Scan/Analyze/Lock your token contract features to enhance security and build trust with your community.</p>
                </div>
                <Button asChild variant="ghost" className="w-full p-0 h-auto hover:bg-transparent">
                  <Link to="/smartcontractlocker" className="block">
                    <img 
                      src={theme === 'light'
                        ? "/MdbApp Screenshots Full/3_MdbApp.io_SmartContractLocker_light.jpeg"
                        : "/MdbApp Screenshots Full/3_MdbApp.io_SmartContractLocker.jpeg"
                      }
                      alt="MdbApp Smart Contract Locker - Secure your tokens and liquidity with our advanced contract locking system at mdbapp.io/smartcontractlocker" 
                      className="w-full h-[300px] object-cover object-top rounded-lg border-2 border-[#0A74FF]/20 hover:border-[#0A74FF]/40 transition-colors"
                    />
                  </Link>
                </Button>
              </div>
              {/* Contract Scanner Screenshot */}
              <div className="relative">
                <div className="mb-4 text-center">
                  <h3 className="text-xl font-semibold mb-2 text-[#00FF00]">Contract Scanner</h3>
                  <p className="text-gray-600 text-sm">Analyze and verify contract features before interacting. Scan/Analyze/Review your contracts features.</p>
                </div>
                <Button asChild variant="ghost" className="w-full p-0 h-auto hover:bg-transparent">
                  <Link to="/mdbmatrixcontractscan" className="block">
                    <img 
                      src={theme === 'light'
                        ? "/MdbApp Screenshots Full/4_mdbapp.io_ContractScanner_light.jpeg"
                        : "/MdbApp Screenshots Full/4_mdbapp.io_ContractScanner.jpeg"
                      }
                      alt="MdbApp Contract Scanner - Analyze and verify smart contracts with our comprehensive scanning tool at mdbapp.io/mdbmatrixcontractscan" 
                      className="w-full h-[300px] object-cover object-top rounded-lg border-2 border-[#00FF00]/20 hover:border-[#00FF00]/40 transition-colors"
                    />
                  </Link>
                </Button>
              </div>
              {/* Token Logo Manager Screenshot */}
              <div className="relative">
                <div className="mb-4 text-center">
                  <h3 className="text-xl font-semibold mb-2">
                    <span className="text-[#9333EA]">Logo</span>{" "}
                    <span className="bg-gradient-to-r from-[#3B82F6] to-[#9333EA] bg-clip-text text-transparent">Manager</span>
                  </h3>
                  <p className="text-gray-600 text-sm">Manage and update your token logos. Update your token logos instantly in your MetaMask browser Wallet.</p>
                </div>
                <Button asChild variant="ghost" className="w-full p-0 h-auto hover:bg-transparent">
                  <Link to="/tokenlogo" className="block">
                    <img 
                      src={theme === 'light'
                        ? "/MdbApp Screenshots Full/5_MdbApp.io TokenLogo_Manager_light.jpeg"
                        : "/MdbApp Screenshots Full/5_MdbApp.io TokenLogo_Manager.jpeg"
                      }
                      alt="MdbApp Logo Manager - Update and manage your token logos with our intuitive logo management system at mdbapp.io/tokenlogo" 
                      className="w-full h-[300px] object-cover object-top rounded-lg border-2 border-[#9333EA]/20 hover:border-[#9333EA]/40 transition-colors"
                    />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Liquidity Management Section */}
      <FadeInSection direction="up">
        <section className="py-8 px-4 bg-gradient-to-b from-background to-muted/20">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Droplets className="h-8 w-8 text-primary" />
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Liquidity Management</h2>
                <Droplets className="h-8 w-8 text-primary" />
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-6">
                Launch pools, add liquidity, or remove liquidity for your tokens
              </p>
              
              {/* DEX Logos */}
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8 mt-6 px-4">
                <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
                  <img 
                    src="/Pancakeswap logo MdbApp.png" 
                    alt="PancakeSwap" 
                    className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
                  />
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">PancakeSwap</span>
                </div>
                <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
                  <img 
                    src="/Uniswap Logo.png" 
                    alt="Uniswap" 
                    className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
                  />
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">Uniswap</span>
                </div>
                <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
                  <img 
                    src="/Trader Joe Logo MdbApp.png" 
                    alt="Trader Joe" 
                    className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
                  />
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">Trader Joe</span>
                </div>
                <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
                  <img 
                    src="/DragonSwap Logo MdbApp.io.png" 
                    alt="DragonSwap" 
                    className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
                  />
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">DragonSwap</span>
                </div>
                <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
                  <img 
                    src="/QuickSwap Logo.png" 
                    alt="QuickSwap" 
                    className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
                  />
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">QuickSwap</span>
                </div>
                <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
                  <img 
                    src="/Veledrome V2.png" 
                    alt="Velodrome" 
                    className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
                  />
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">Velodrome</span>
                </div>
              </div>
            </div>

            {/* Liquidity Feature Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {/* Launch Pool Card */}
              <Card className="border-primary/20 hover:border-primary/40 transition-all hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Droplets className="h-5 w-5 text-primary" />
                    <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
                      Launch Pool
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Create new liquidity pools for your tokens on PancakeSwap, Uniswap, or Trader Joe.
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>✓ V2 & V3 protocol support</li>
                    <li>✓ Custom fee tiers (V3)</li>
                    <li>✓ Multi-network support</li>
                    <li>✓ Instant pool creation</li>
                  </ul>
                  <Button asChild className="w-full mt-4">
                    <Link to="/liquidity" className="flex items-center justify-center gap-2">
                      <Droplets className="h-4 w-4" />
                      Launch Pool
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Add Liquidity Card */}
              <Card className="border-green-500/20 hover:border-green-500/40 transition-all hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Plus className="h-5 w-5 text-green-500" />
                    <span className="bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">
                      Add Liquidity
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Increase liquidity in existing pools to boost trading depth and earn fees.
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>✓ Earn trading fees</li>
                    <li>✓ Increase pool depth</li>
                    <li>✓ NFT positions (V3)</li>
                    <li>✓ LP tokens (V2)</li>
                  </ul>
                  <Button asChild variant="outline" className="w-full mt-4 border-green-500/50 hover:bg-green-500/10">
                    <Link to="/liquidity" className="flex items-center justify-center gap-2">
                      <Plus className="h-4 w-4" />
                      Add Liquidity
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Remove Liquidity Card */}
              <Card className="border-red-500/20 hover:border-red-500/40 transition-all hover:shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Minus className="h-5 w-5 text-red-500" />
                    <span className="bg-gradient-to-r from-red-500 to-orange-600 bg-clip-text text-transparent">
                      Remove Liquidity
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Withdraw your liquidity from pools to retrieve tokens and earned fees.
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>✓ Claim earned fees</li>
                    <li>✓ Partial or full removal</li>
                    <li>✓ Flexible percentages</li>
                    <li>✓ Instant withdrawal</li>
                  </ul>
                  <Button asChild variant="outline" className="w-full mt-4 border-red-500/50 hover:bg-red-500/10">
                    <Link to="/liquidity" className="flex items-center justify-center gap-2">
                      <Minus className="h-4 w-4" />
                      Remove Liquidity
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* View All Positions Link */}
            <div className="text-center mt-8">
              <Button asChild size="lg" variant="outline">
                <Link to="/liquidity" className="flex items-center gap-2">
                  <Wallet className="h-5 w-5" />
                  View My Positions
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* FAQ Section */}
      <FadeInSection direction="up">
        <section className="py-12">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 tracking-tight text-center">
                Frequently Asked Questions
              </h2>
              <div className="rounded-lg overflow-hidden border bg-card" style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}>
                {faqData.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Guides & Blog Section */}
      <FadeInSection direction="right">
        <section className="py-12">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 tracking-tight text-center">
                Guides & Blog
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/create-token-guide"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-blue-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-blue-500/10' : 'bg-white hover:bg-blue-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(59, 130, 246, 0.3)', borderColor: 'rgba(59, 130, 246, 0.5)' }}
                  >
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Create Token Guide
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/create-meme-coin"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-orange-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-orange-500/10' : 'bg-white hover:bg-orange-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(249, 115, 22, 0.3)', borderColor: 'rgba(249, 115, 22, 0.5)' }}
                  >
                    <RocketCoin className="w-5 h-5 text-orange-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Create Meme Coin Guide
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/create-reflection-token"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-green-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-green-500/10' : 'bg-white hover:bg-green-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.3)', borderColor: 'rgba(34, 197, 94, 0.5)' }}
                  >
                    <Coins className="w-5 h-5 text-green-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Create Reflection Token
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/create-auto-liquidity-token"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-yellow-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-yellow-500/10' : 'bg-white hover:bg-yellow-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(234, 179, 8, 0.3)', borderColor: 'rgba(234, 179, 8, 0.5)' }}
                  >
                    <Droplet className="w-5 h-5 text-yellow-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Create Auto-Liquidity Token
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/create-auto-burn-token"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-red-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-red-500/10' : 'bg-white hover:bg-red-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(239, 68, 68, 0.3)', borderColor: 'rgba(239, 68, 68, 0.5)' }}
                  >
                    <Flame className="w-5 h-5 text-red-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Create Auto-Burn Token
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/matrix-contract-scanner"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-emerald-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-emerald-500/10' : 'bg-white hover:bg-emerald-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(16, 185, 129, 0.3)', borderColor: 'rgba(16, 185, 129, 0.5)' }}
                  >
                    <Search className="w-5 h-5 text-emerald-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Matrix Contract Scanner
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/token-tools"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-cyan-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-cyan-500/10' : 'bg-white hover:bg-cyan-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(6, 182, 212, 0.3)', borderColor: 'rgba(6, 182, 212, 0.5)' }}
                  >
                    <Settings className="w-5 h-5 text-cyan-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Token Tools Guide
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/contract-locker"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-yellow-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-yellow-500/10' : 'bg-white hover:bg-yellow-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(234, 179, 8, 0.3)', borderColor: 'rgba(234, 179, 8, 0.5)' }}
                  >
                    <Lock className="w-5 h-5 text-yellow-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Contract Locker Guide
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/automatic-contract-verification"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-purple-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-purple-500/10' : 'bg-white hover:bg-purple-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(168, 85, 247, 0.3)', borderColor: 'rgba(168, 85, 247, 0.5)' }}
                  >
                    <CheckCircle className="w-5 h-5 text-purple-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Contract Verification
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/smart-contract-locking"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-indigo-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-indigo-500/10' : 'bg-white hover:bg-indigo-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(99, 102, 241, 0.3)', borderColor: 'rgba(99, 102, 241, 0.5)' }}
                  >
                    <LockIcon className="w-5 h-5 text-indigo-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Smart Contract Locking
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/token-economics"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-pink-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-pink-500/10' : 'bg-white hover:bg-pink-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(236, 72, 153, 0.3)', borderColor: 'rgba(236, 72, 153, 0.5)' }}
                  >
                    <BarChart3Icon className="w-5 h-5 text-pink-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Token Economics
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/token-security"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-teal-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-teal-500/10' : 'bg-white hover:bg-teal-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(20, 184, 166, 0.3)', borderColor: 'rgba(20, 184, 166, 0.5)' }}
                  >
                    <Shield className="w-5 h-5 text-teal-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Token Security
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/add-token-logo"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-rose-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-rose-500/10' : 'bg-white hover:bg-rose-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(244, 63, 94, 0.3)', borderColor: 'rgba(244, 63, 94, 0.5)' }}
                  >
                    <FileCode2 className="w-5 h-5 text-rose-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Add Token Logo
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>

                <Button asChild variant="ghost" className="h-auto p-0">
                  <Link
                    to="/blog/best-cold-storage-wallets"
                    className={`
                      w-full flex flex-col items-center text-center gap-3 p-4 rounded-lg
                      border border-sky-500/30
                      ${theme === 'dark' ? 'bg-transparent hover:bg-sky-500/10' : 'bg-white hover:bg-sky-50'}
                      transition-all duration-200
                      group
                    `}
                    style={{ boxShadow: '0 0 15px rgba(14, 165, 233, 0.3)', borderColor: 'rgba(14, 165, 233, 0.5)' }}
                  >
                    <Wallet className="w-5 h-5 text-sky-500" />
                    <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                      Best Cold Storage Wallets
                    </span>
                    <ArrowRight className={`
                      ml-auto w-5 h-5 
                      ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}
                      group-hover:translate-x-1 transition-transform duration-200
                    `} />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Launch Your Token?
              </h1>
              <p className="text-xl mb-8 text-muted-foreground">
                Create & manage your coin now with the most trusted launch platform
              </p>
              <div className="flex justify-center gap-4 flex-wrap mb-6 mt-4">
                <Button asChild size="lg" className="bg-transparent hover:bg-transparent border-[var(--color-header-accent,#0A74FF)] border text-foreground group">
                  <Link to="/create" className="flex items-center">
                    Create Your Token <RocketCoin className="ml-2 w-5 h-5 [color:#EAB308] group-hover:[color:#F59E0B]" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/blog" className="flex items-center">
                    Read Guides <BookOpen className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Combined DappRadar and DappBay Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">Now listed on DappRadar & the Official Binance BSCChain's DappBay</h2>
          <div className="mb-6 flex justify-center items-center gap-8">
            <a 
              href="https://dappradar.com/dapp/mdbapp"
              target="_blank"
              rel="noopener noreferrer dofollow"
              className="inline-block hover:opacity-90 transition-opacity"
            >
              <img 
                src="/DappRadarLogoMdbApp.webp" 
                alt="DappRadar Logo" 
                className="h-12 w-auto rounded-lg"
              />
            </a>
            <a 
              href="https://dappbay.bnbchain.org/detail/mdbapp"
              target="_blank"
              rel="noopener noreferrer dofollow"
              className="inline-block hover:opacity-90 transition-opacity"
            >
              <img 
                src="/DappBayBSCMdbApp.webp" 
                alt="Binance Logo" 
                className="h-12 w-auto"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Create SPL Tokens on Solana Section */}
      <FadeInSection direction="left">
        <section className="py-12 px-4">
          <div className="container mx-auto text-center">
            <div className="flex justify-center items-center gap-4 mb-6">
              <img 
                src="/Size=96, Color=color@2x.png" 
                alt="Solana Logo" 
                className="h-10 w-auto"
              />
              <h2 className="text-2xl font-bold">Create SPL Tokens on Solana</h2>
              <img 
                src="/Size=96, Color=color@2x.png" 
                alt="Solana Logo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="flex justify-center mb-8">
              <a 
                href="https://solanatokenlauncher.app/"
                target="_blank"
                rel="noopener noreferrer dofollow"
                className="inline-block hover:opacity-90 transition-opacity"
              >
                <img 
                  src="/logo512.png" 
                  alt="Solana Token Creator App" 
                  className="h-[89.4px] w-auto rounded-lg"
                />
              </a>
            </div>
            <div className="max-w-4xl mx-auto">
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                Create SPL tokens on Solana in seconds with this intuitive dApp. Design, mint, & launch your crypto token with metadata and logo—no coding required
              </p>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                The ultimate Solana token generator for creating custom SPL tokens, meme coins, and crypto assets on the Solana blockchain. Launch your token with custom supply, decimals, and metadata.
              </p>
              <div className="mt-6">
                <Button asChild variant="outline" className="gap-2 bg-gradient-to-r from-purple-600 via-purple-400 to-green-400 hover:from-purple-700 hover:via-purple-500 hover:to-green-500 text-white border-none">
                  <a href="https://solanatokenlauncher.app/" target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <Rocket className="mr-2 w-5 h-5 text-white" /> Launch Solana Token Launcher
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* Create Tokens on SUI Section */}
      <FadeInSection direction="right">
        <section className="py-12 px-4">
          <div className="container mx-auto text-center">
            <div className="flex justify-center items-center gap-4 mb-6">
              <img 
                src="/Blu sui standard.png" 
                alt="SUI Logo" 
                className="h-10 w-auto"
              />
              <h2 className="text-2xl font-bold">Create Tokens on SUI</h2>
              <img 
                src="/Blu sui standard.png" 
                alt="SUI Logo" 
                className="h-10 w-auto"
              />
            </div>
            <div className="flex justify-center mb-8">
              <a 
                href="https://suitokenlauncher.app/"
                target="_blank"
                rel="noopener noreferrer dofollow"
                className="inline-block hover:opacity-90 transition-opacity"
              >
                <img 
                  src="/Bluey Suey.png" 
                  alt="SUI Token Creator App" 
                  className="h-[89.4px] w-auto rounded-lg"
                />
              </a>
            </div>
            <div className="max-w-4xl mx-auto">
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                Create tokens on SUI in seconds with this intuitive dApp. Design, mint, & launch your crypto token with metadata and logo—no coding required
              </p>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                The ultimate SUI token generator for creating custom tokens, meme coins, and crypto assets on the SUI blockchain. Launch your token with custom supply, decimals, and metadata.
              </p>
              <div className="mt-6">
                <Button asChild variant="outline" className="gap-2 bg-gradient-to-r from-blue-600 via-blue-400 to-cyan-400 hover:from-blue-700 hover:via-blue-500 hover:to-cyan-500 text-white border-none">
                  <a href="https://suitokenlauncher.app/" target="_blank" rel="noopener noreferrer" className="flex items-center">
                    <Rocket className="mr-2 w-5 h-5 text-white" /> Launch SUI Token Launcher
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* MDBTDJ Merch Shop Section */}
      <FadeInSection direction="up">
        <section className="py-12 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">MDBTDJ Merch Shop</h2>
            <div className="flex justify-center mb-8">
              <a 
                href="https://tattooeddjs.com"
                target="_blank"
                rel="noopener noreferrer dofollow"
                className="inline-block hover:opacity-90 transition-opacity"
              >
                <img 
                  src="/FAVI 250.png" 
                  alt="MDBTDJ Merch Shop" 
                  className="h-20 w-auto"
                />
              </a>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-center gap-8 mb-4">
                <img 
                  src="/TattooeddjsLogoMdbApp250x250.png" 
                  alt="Tattooed Dj's Logo" 
                  className="w-8 h-8 object-contain"
                />
                <h3 className="text-xl font-bold">Tattooed Dj's NFT Project</h3>
                <img 
                  src="/TattooeddjsLogoMdbApp250x250.png" 
                  alt="Tattooed DJs Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Discover The Tattooed Dj's Exclusive NFT Collection and merchandise shop, featuring digital art pieces that blend various arts and technologies! 
                Each NFT captures the authenticity and energy that define Tattooed Dj's in the realm of blockchain and art. 
                999 1 of 1 UNIQUE Designs! Stay tuned for the NFT's launch!
              </p>
            </div>
          </div>
        </section>
      </FadeInSection>

      {/* AiFactCheck.App Section */}
      <FadeInSection direction="down">
        <section className="py-12 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">AiFactCheck.App</h2>
            <div className="flex justify-center mb-8">
              <a 
                href="https://aifactcheck.app"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:opacity-90 transition-opacity"
              >
                <img 
                  src="/authors/Ai Fact Check icon Smile- Transparent.png" 
                  alt="AiFactCheck.App" 
                  className="h-20 w-auto"
                />
              </a>
            </div>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                AI-powered fact-checking for claims, sources, and political bias analysis.
              </p>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                AiFactCheck.App uses advanced AI to verify claims, check sources, and analyze political bias, helping you navigate information with confidence.
              </p>
              <div className="text-center max-w-3xl mx-auto space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-3">Key Features:</h3>
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold mb-2 text-blue-600 dark:text-blue-400">Fact Check</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Verify claims, posts, news articles, or text content<br />
                    Real-time fact verification with AI<br />
                    Cross-reference with multiple trusted sources
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-blue-600 dark:text-blue-400">Bias Check</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Analyze political bias in content<br />
                    Identify potential misinformation patterns<br />
                    Get balanced perspective on news and claims
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-blue-600 dark:text-blue-400">AI-Powered Analysis</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Text, image, and news/blog URL analysis<br />
                    AI-Detect feature for content verification<br />
                    Comprehensive fact-checking reports
                  </p>
                </div>

                <div className="mt-8">
                  <a 
                    href="https://aifactcheck.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 dark:bg-black/30 hover:bg-white/20 dark:hover:bg-black/40 border border-blue-600 text-blue-600 dark:text-blue-400 font-semibold rounded-lg transition-all"
                  >
                    <img 
                      src="/authors/Ai Fact Check icon Smile- Transparent.png" 
                      alt="AiFactCheck" 
                      className="h-6 w-6"
                    />
                    Visit AiFactCheck.App
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      <FadeInSection direction="up">
        <section className="py-12 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">All In One Ai</h2>
            <div className="flex justify-center mb-8">
              <div className="inline-block">
                <img 
                  src="/authors/AllInOneAi.App Logo MdbApp.io.PNG" 
                  alt="All In One Ai" 
                  className="h-20 w-auto"
                />
              </div>
            </div>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-4 leading-relaxed font-semibold">
                One workspace, dozens of AI superpowers
              </p>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                Chat, code, generate images and video, fact check, learn, and debate - all from a single unified canvas powered by AI.
              </p>
              <div className="text-center max-w-3xl mx-auto space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-3">What you can do:</h3>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-blue-600 dark:text-blue-400">Chat & Code</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Talk with AI like a teammate, draft content, and write or refactor code in one place.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-blue-600 dark:text-blue-400">Create & Visualize</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Generate images and video concepts, explore ideas visually, and refine your projects faster.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold mb-2 text-blue-600 dark:text-blue-400">Learn, Fact Check & Debate</h4>
                  <p className="text-gray-600 dark:text-gray-300">
                    Research topics, verify information, and compare perspectives side-by-side in a unified AI workspace.
                  </p>
                </div>
                <div className="mt-8">
                  <a 
                    href="https://allinoneai.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 dark:bg-black/30 hover:bg-white/20 dark:hover:bg-black/40 border border-blue-600 text-blue-600 dark:text-blue-400 font-semibold rounded-lg transition-all"
                  >
                    <img 
                      src="/authors/AllInOneAi.App Logo MdbApp.io.PNG" 
                      alt="All In One Ai" 
                      className="h-6 w-6"
                    />
                    Visit All In One Ai
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </FadeInSection>

      <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
      </div>

      <div className="mt-12 text-center">
      </div>
    </>
  );
}
